// `useUiLabels()` — a reactive bag of the reserved `ui.*` labels (contract 04 §9, §13.2). It is
// the resolution of module 02's deferred open question (contract §18): module 05 `provide()`s the
// bag once at the app root, and `shared/ui` primitives `inject` ALREADY-RESOLVED strings — so a
// primitive renders its sr-only / placeholder copy WITHOUT importing vue-i18n.
//
// Three pieces make that contract work:
//   • `useUiLabels()`      — resolves each `ui.*` key into a reactive string (module 05 provides it).
//   • `provideUiLabels()`  — the composition-root convenience that installs the bag under the key.
//   • `injectUiLabels()`   — what a primitive calls; returns the provided bag, or the KEY-PATH
//                            fallback when no provider exists (Histoire, isolated tests) so a
//                            missing provider is dev-visible (`ui.close`) not a silent blank
//                            (contract §18 decision).

import { computed, inject, provide, reactive, type ComputedRef, type InjectionKey } from 'vue'
import { useI18n } from 'vue-i18n'

/** Reserved-key → message-path map. The single source for both the resolved bag and the fallback. */
const UI_LABEL_PATHS = {
  close: 'ui.close',
  search: 'ui.search',
  select: 'ui.select',
  selectAll: 'ui.selectAll',
  loading: 'ui.loading',
  noResults: 'ui.noResults',
  clear: 'ui.clear',
  remove: 'ui.remove',
  resize: 'ui.resize',
  paginationOf: 'ui.pagination.of',
} as const

type UiLabelKey = keyof typeof UI_LABEL_PATHS

/** The reserved `ui.*` labels a `shared/ui` primitive consumes, as resolved (reactive) strings. */
export type UiLabels = Readonly<Record<UiLabelKey, string>>

/**
 * Key-path fallback bag used when no provider is installed (contract §18): a primitive reads
 * `ui.close` verbatim — a visible bug report in a story, never a silent empty string.
 */
export const UI_LABELS_FALLBACK: UiLabels = { ...UI_LABEL_PATHS }

/** Injection key module 05 provides against and primitives inject. */
export const UI_LABELS_INJECTION_KEY: InjectionKey<UiLabels> = Symbol('orphex.uiLabels')

/**
 * Resolve the reserved `ui.*` keys into a reactive bag. Each property re-resolves when the active
 * locale changes. Requires the i18n runtime (calls `useI18n`); primitives never call this directly
 * — they {@link injectUiLabels} the bag module 05 {@link provideUiLabels provides}.
 */
export function useUiLabels(): UiLabels {
  const { t } = useI18n({ useScope: 'global' })
  const bag = {} as Record<UiLabelKey, ComputedRef<string>>
  for (const key of Object.keys(UI_LABEL_PATHS) as UiLabelKey[]) {
    bag[key] = computed(() => t(UI_LABEL_PATHS[key]))
  }
  // `reactive` unwraps the computed refs, exposing plain reactive strings to consumers.
  return reactive(bag) as UiLabels
}

/**
 * Install the resolved label bag under {@link UI_LABELS_INJECTION_KEY} (module 05, once at the
 * app root). Returns the bag for convenience. Must run in a component `setup`.
 */
export function provideUiLabels(): UiLabels {
  const labels = useUiLabels()
  provide(UI_LABELS_INJECTION_KEY, labels)
  return labels
}

/**
 * Inject the provided `ui.*` label bag, or the key-path {@link UI_LABELS_FALLBACK} when no provider
 * is installed. `shared/ui` primitives call this to read resolved copy without importing vue-i18n.
 */
export function injectUiLabels(): UiLabels {
  return inject(UI_LABELS_INJECTION_KEY, UI_LABELS_FALLBACK)
}
